import { GridOptions } from 'ag-grid-community';
import React, { ComponentType, FC } from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import {
  ColumnsProps,
  ExternalFilterProps,
  SelectionsProps,
  SingleClickEditProps,
} from 'components/atoms/grid/plugins';
import { RowsProps } from 'components/atoms/grid/plugins/with-rows';
import { EditorPopupsProps } from '../with-editor-popups';
import { useFilter, useIsSelectable, useOnCellClicked } from './hooks';
import { useOnPopup } from './use-on-popup';
import { getColumns, postSort } from './utils';

type KnownProps = Pick<GridOptions, 'postSort'>;

export type ProvisionalRowProps = {
  provisionalRow?: Record<string, unknown>;
};

export const withProvisionalRow = <
  T extends KnownProps &
    SingleClickEditProps &
    RowsProps &
    SelectionsProps &
    ExternalFilterProps &
    ColumnsProps &
    EditorPopupsProps,
>(
  Component: ComponentType<T>,
): FC<Omit<T, keyof KnownProps> & ProvisionalRowProps> => {
  const Next: FC<Omit<T, keyof KnownProps> & ProvisionalRowProps> = ({
    filter: actualFilter,
    isSelectable: actualIsSelectable,
    onCellClick: actualOnCellClicked,
    columns: actualColumns,
    rows: actualRows,
    provisionalRow: addRow,
    onPopup: actualOnPopup,
    ...props
  }) => {
    const filter = useFilter(actualFilter, addRow);
    const isSelectable = useIsSelectable(actualIsSelectable);
    const rows = addRow != null ? actualRows?.concat(addRow) : actualRows;
    const onCellClicked = useOnCellClicked(actualOnCellClicked);
    const columns = addRow == null ? actualColumns : getColumns(actualColumns);
    const onPopup = useOnPopup(actualOnPopup);

    return (
      <Component
        {...(props as any)}
        filter={filter}
        isSelectable={isSelectable}
        rows={rows}
        onCellClick={onCellClicked}
        postSort={postSort}
        columns={columns}
        onPopup={onPopup}
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithProvisionalRow'))(Next);
};
